import React, { useCallback, useState } from 'react';
import { useSelector } from "react-redux";
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import imageCompression from 'browser-image-compression';

const API_BASE_URL = process.env.REACT_APP_API_URL;

const FileUploader = ({ 
    files = [], 
    onFilesChange, 
    validateBeforeUpload = null,
    multiple = true, 
    acceptImagesOnly = false,
    uploadText = "Upload Files",
    acceptTypes = undefined
}) => {
    const { auth } = useSelector((state) => state.auth);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });

    const onDrop = useCallback(
        async (acceptedFiles) => {
            // Validate before starting upload process
            if (validateBeforeUpload && !validateBeforeUpload(acceptedFiles)) {
                return; // Stop if validation fails
            }

            setUploading(true);
            setUploadProgress({ current: 0, total: acceptedFiles.length });
            
            try {
                // Compress images before upload
                const compressedFiles = await Promise.all(
                    acceptedFiles.map(async (file) => {
                        // Only compress images
                        if (file.type.startsWith('image/')) {
                            const options = {
                                maxSizeMB: 0.5, // Maximum file size in MB
                                maxWidthOrHeight: 1920, // Maximum width or height
                                useWebWorker: true, // Use web worker for better performance
                                quality: 0.1 // Quality (0.1 to 1)
                            };
                            
                            try {
                                const compressedFile = await imageCompression(file, options);
                                console.log(`Original: ${(file.size / 1024 / 1024).toFixed(2)}MB, Compressed: ${(compressedFile.size / 1024 / 1024).toFixed(2)}MB`);
                                return compressedFile;
                            } catch (compressionError) {
                                console.warn('Compression failed, using original file:', compressionError);
                                return file;
                            }
                        }
                        return file; // Return non-image files as-is
                    })
                );

                // Upload files one by one to avoid 413 error
                const token = auth.token;
                let currentFileList = [...files]; // Keep track of current files

                for (let i = 0; i < compressedFiles.length; i++) {
                    setUploadProgress({ current: i + 1, total: compressedFiles.length });
                    
                    const formData = new FormData();
                    formData.append('images', compressedFiles[i]);

                    try {
                        const response = await axios.post(`${API_BASE_URL}/files/upload-images`, formData, {
                            headers: {
                                'Content-Type': 'multipart/form-data',
                                'Authorization': `Bearer ${token}`
                            },
                        });

                        const uploaded = response.data?.data?.rows.map((f) => f.location) || [];
                        
                        // Update file list immediately after each successful upload
                        if (uploaded.length > 0) {
                            if (multiple) {
                                currentFileList = [...currentFileList, ...uploaded];
                            } else {
                                currentFileList = uploaded;
                            }
                            onFilesChange(currentFileList);
                        }
                    } catch (error) {
                        console.error(`Upload error for file ${i + 1}:`, error);
                        // Continue with other files even if one fails
                    }
                }
            } catch (error) {
                console.error('Upload error:', error);
            } finally {
                setUploading(false);
                setUploadProgress({ current: 0, total: 0 });
            }
        },
        [files, onFilesChange, multiple, auth.token, validateBeforeUpload]
    );

    const handleRemove = (index) => {
        const updated = [...files];
        updated.splice(index, 1);
        onFilesChange(updated);
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        multiple,
        accept: acceptTypes || (acceptImagesOnly ? { 'image/*': [] } : undefined),
    });

    return (
        <div>
            {/* Dropzone Area */}
            <div
                {...getRootProps()}
                className={`border rounded p-4 text-center mb-3 ${
                    isDragActive ? 'border-primary' : ''
                }`}
                style={{ minHeight: 154, cursor: 'pointer', backgroundColor: 'rgba(149, 149, 149, 0.05)', border: '0.063rem dashed #DDDFE1' }}
            >
                <input {...getInputProps()} />
                <i style={{ fontSize: '50px', color: '#eee' }} className="fas fa-cloud-upload-alt"></i>
                <p className="fw-bold mb-0">
                    {uploading 
                        ? `Uploading ${uploadProgress.current}/${uploadProgress.total} files...` 
                        : uploadText
                    }
                </p>
                <small className="text-muted">
                    Images will be automatically compressed and uploaded one by one
                </small>
            </div>

            {/* Previews */}
            <div className="d-flex flex-wrap gap-3">
                {files.map((file, index) => {
                    const url = typeof file === 'string' ? file : URL.createObjectURL(file);
                    const isVideo = url.match(/\.(mp4|webm|ogg)$/i);

                    return (
                        <div key={index} className="position-relative">
                            {!acceptImagesOnly && isVideo ? (
                                <video src={url} width="100" height="100" controls />
                            ) : (
                                <img
                                    src={url}
                                    alt={`preview-${index}`}
                                    width="100"
                                    height="100"
                                    className="rounded"
                                    onError={(e) => (e.target.style.display = 'none')}
                                />
                            )}
                            <button
                            type="button"
                            className="btn-close position-absolute top-0 end-0"
                            aria-label="Remove"
                            onClick={() => handleRemove(index)}
                            style={{
                                backgroundColor: 'white',
                                borderRadius: '50%',
                                width: '1.25rem',
                                height: '1.25rem',
                                padding: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 0 3px rgba(0,0,0,0.2)',
                                marginTop: '3px',
                                marginRight: '2px'
                            }}
                            ></button>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default FileUploader;
